import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Zap } from 'lucide-react';
import { DatePicker } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';
import { cn } from '@/utils/merge';
import { ActivityDecisionModal } from './ActivityDecisionModal';
import {
	ACTIVITY_EVENT,
	ACTIVITY_GROUPS,
	TONE_CLASSES,
	actorLabel,
	describeActivity,
	formatClock,
	groupOfEvent,
	pulledSweepSeconds,
	toneOf,
} from './activityLabels';
import { selectableDays } from './activityRange.js';

// What has happened in this city, in order.
//
// The board answers "what is the state of things"; this answers "how did it get
// there", which nothing on the page could say before. The socket that drives the
// board sends `order-status-change` for sixteen different actions with no verb
// attached, so the history had to be built server-side — see util/order-log.js.
//
// Newest first. A dispatcher looks here when something surprises them, and the
// surprising thing is always the most recent one.
const rowTone = event => TONE_CLASSES[toneOf(event.type)] || TONE_CLASSES.slate;

// Only an automatic assignment has reasoning behind it. A human's choice and a
// rider's own pick were decided by a person, and offering to explain them would
// promise something that does not exist.
const hasReasoning = event =>
	event.type === ACTIVITY_EVENT.ASSIGNED && event.meta?.assignedBy === 'auto' && !!event.orderId;

// Shrunk to sit in a row of 24px controls — a MUI text field is 56px tall by
// default, which would double the height of the filter bar on its own.
// `clearable` puts an × in the field, which is how the filter is switched off
// one end at a time — «Ζωντανά» clears both at once.
const dateSlotProps = {
	textField: { size: 'small' },
	field: { clearable: true },
};

const dateSx = {
	width: 132,
	'& .MuiInputBase-root': { height: 24, fontSize: 12, background: '#fff' },
	'& .MuiInputBase-input': { padding: '0 0 0 6px' },
	'& .MuiIconButton-root': { padding: '2px' },
	'& .MuiSvgIcon-root': { fontSize: 15 },
};

export const ActivityFeed = ({
	events,
	loading,
	error,
	allDeliveries,
	// The date range is the one filter that is NOT applied here: it is a server
	// query, because the board only holds the last few hours and «χθες» is not in
	// memory to be filtered. See useActivityFeed.
	range = { from: '', to: '' },
	setRange,
	filteringByDate = false,
	retentionDays = 7,
}) => {
	const [open, setOpen] = useState(true);
	// Group keys that are switched OFF. Empty means everything, which is the
	// state someone wants until they are looking for one thing in particular.
	const [hidden, setHidden] = useState([]);
	const [riderId, setRiderId] = useState('');
	const [shopName, setShopName] = useState('');
	const [orderQuery, setOrderQuery] = useState('');
	const [decisionOrderId, setDecisionOrderId] = useState(null);

	// The pickers are bounded by what the table actually holds: rows older than
	// the retention window are gone, and offering a date that can only ever come
	// back empty reads as the feature being broken.
	const { today, oldestSelectableDay } = useMemo(() => {
		const days = selectableDays({ retentionDays });
		return {
			today: DateTime.fromISO(days.newest),
			oldestSelectableDay: DateTime.fromISO(days.oldest),
		};
	}, [retentionDays]);

	// The picker speaks Luxon, the query speaks 'YYYY-MM-DD'.
	//
	// `null` is the field being cleared and must reach the state — that is how the
	// filter is switched off. A half-typed date arrives as an invalid DateTime and
	// is IGNORED rather than written as empty, which would flip the board back to
	// live and refetch on the way to every date.
	const setDay = (key, value) => {
		if (value === null) {
			setRange(r => ({ ...r, [key]: '' }));
			return;
		}
		if (value?.isValid) {
			setRange(r => ({ ...r, [key]: value.toISODate() }));
		}
	};

	// Current names beat the ones frozen into the log — a renamed rider must not
	// read as two people halfway down the feed.
	const deliveryById = useMemo(
		() => Object.fromEntries((allDeliveries || []).map(d => [d.id, d])),
		[allDeliveries],
	);

	// Built from what the feed has actually seen rather than from the city's shop
	// list: a shop with no activity is a filter that returns nothing.
	const shopNames = useMemo(
		() => [...new Set(events.map(e => e.meta?.shopName).filter(Boolean))].sort(),
		[events],
	);

	const shown = useMemo(() => {
		const order = orderQuery.replace(/\D/g, '');

		const visible = events.filter(e => {
			if (hidden.includes(groupOfEvent(e.type))) return false;
			if (riderId && `${e.deliveryId}` !== `${riderId}`) return false;
			if (shopName && e.meta?.shopName !== shopName) return false;
			// Substring rather than equality: the id is read off the board and typed
			// in a hurry, and "447" finding #4471 is the useful behaviour.
			// `||` and not `??`: rows written before the order-log fix carry 0 for
			// "no order", and `'0'.includes('0')` made typing 0 match every shift.
			if (order && !`${e.orderId || ''}`.includes(order)) return false;
			return true;
		});
		// Sorted here rather than trusted from either source: the backfill arrives
		// chronologically and the socket appends, but a reconnect can interleave
		// the two. Reversed for display, newest at the top.
		return [...visible]
			.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt) || a.id - b.id)
			.reverse();
	}, [events, hidden, riderId, shopName, orderQuery]);

	const toggleGroup = key =>
		setHidden(prev => (prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]));

	return (
		<div className='mt-3 rounded-md border border-slate-200 bg-white'>
			<div
				className='flex cursor-pointer flex-row items-center gap-x-2 px-2.5 py-1.5'
				onClick={() => setOpen(o => !o)}
			>
				{open ? (
					<ChevronDown size={15} className='shrink-0 text-slate-400' />
				) : (
					<ChevronRight size={15} className='shrink-0 text-slate-400' />
				)}
				<span className='text-[13px] font-semibold tracking-wider text-slate-500 uppercase'>
					Χρονολόγιο
				</span>
				{/* Desktop only, at the same 861px the feed's own spacing uses. On a
				    phone the header row is already tight and the count is the least
				    useful thing in it. */}
				<span className='hidden text-[13px] text-slate-400 min-[861px]:inline'>
					{loading ? 'φόρτωση…' : `${shown.length} συμβάντα`}
				</span>

				{/* The newest line, readable with the panel shut — which is how it will
				    sit most of the time on a busy board. */}
				{!open && shown[0] && (
					<span className='min-w-0 truncate text-[13px] text-slate-500'>
						{describeActivity(shown[0], { deliveryById }).title}
					</span>
				)}
			</div>

			{open && (
				<div className='border-t border-slate-200'>
					<div className='flex flex-row flex-wrap items-center gap-1.5 px-2.5 py-1.5'>
						<select
							className='h-6 rounded border border-slate-300 px-1 text-[12px] outline-none focus:border-slate-500'
							value={riderId}
							onChange={e => setRiderId(e.target.value)}
						>
							<option value=''>Όλοι οι διανομείς</option>
							{(allDeliveries || []).map(d => (
								<option key={d.id} value={d.id}>
									{`${d.firstname || ''} ${d.lastname || ''}`.trim() || d.id}
								</option>
							))}
						</select>

						<select
							className='h-6 rounded border border-slate-300 px-1 text-[12px] outline-none focus:border-slate-500'
							value={shopName}
							onChange={e => setShopName(e.target.value)}
						>
							<option value=''>Όλα τα καταστήματα</option>
							{shopNames.map(n => (
								<option key={n} value={n}>
									{n}
								</option>
							))}
						</select>

						<input
							className='h-6 w-20 rounded border border-slate-300 px-1 text-[12px] outline-none focus:border-slate-500'
							placeholder='# παραγγ.'
							value={orderQuery}
							onChange={e => setOrderQuery(e.target.value)}
						/>

						{/* Unlike everything else on this row, these two refetch. The board
						    holds a few hours; a past day has to be asked for.

						    MUI's DatePicker rather than <input type='date'>, whose displayed
						    format follows the BROWSER's locale and cannot be set per field —
						    an admin on an en-US Chrome reads 08/13 for the 13th. Same
						    `format='dd/MM/yyyy'` and the same Luxon adapter as the rest of
						    the app (ui/range-picker, MyDateTimePicker). */}
						{setRange && (
							<>
								<span className='w-px self-stretch bg-slate-200' />

								<span className='text-[12px] text-slate-400'>Από</span>
								<DatePicker
									value={range.from ? DateTime.fromISO(range.from) : null}
									onChange={v => setDay('from', v)}
									format='dd/MM/yyyy'
									minDate={oldestSelectableDay}
									maxDate={range.to ? DateTime.fromISO(range.to) : today}
									slotProps={dateSlotProps}
									sx={dateSx}
								/>

								<span className='text-[12px] text-slate-400'>έως</span>
								<DatePicker
									value={range.to ? DateTime.fromISO(range.to) : null}
									onChange={v => setDay('to', v)}
									format='dd/MM/yyyy'
									minDate={range.from ? DateTime.fromISO(range.from) : oldestSelectableDay}
									maxDate={today}
									slotProps={dateSlotProps}
									sx={dateSx}
								/>

								{filteringByDate && (
									<button
										type='button'
										onClick={() => setRange({ from: '', to: '' })}
										className='cursor-pointer rounded border border-slate-300 bg-white px-1.5 py-0.5 text-[12px] leading-[16px] text-slate-600'
									>
										Ζωντανά
									</button>
								)}
							</>
						)}

						<span className='w-px self-stretch bg-slate-200' />

						{ACTIVITY_GROUPS.map(g => {
							const off = hidden.includes(g.key);
							return (
								<button
									key={g.key}
									type='button'
									onClick={() => toggleGroup(g.key)}
									className={cn(
										'cursor-pointer rounded border px-1.5 py-0.5 text-[12px] leading-[16px]',
										off
											? 'border-slate-200 bg-white text-slate-400 line-through'
											: 'border-slate-300 bg-slate-100 text-slate-700',
									)}
								>
									{g.label}
								</button>
							);
						})}
					</div>

					<div className='max-h-[320px] overflow-y-auto border-t border-slate-100'>
						{error && (
							<div className='px-2.5 py-3 text-[13px] text-red-700'>
								Δεν φορτώθηκε το ιστορικό. Τα νέα συμβάντα θα εμφανίζονται κανονικά.
							</div>
						)}

						{!error && !shown.length && (
							<div className='px-2.5 py-3 text-[13px] text-slate-400'>
								{loading
									? 'Φόρτωση…'
									: filteringByDate
										? `Καμία δραστηριότητα σε αυτές τις ημερομηνίες. Κρατάμε ιστορικό ${retentionDays} ημερών.`
										: 'Καμία δραστηριότητα ακόμα.'}
							</div>
						)}

						{/* The server caps a window at 400 rows, newest first. Without this
						    a busy day silently looks like it started at midday. */}
						{filteringByDate && shown.length >= 400 && (
							<div className='border-b border-slate-100 px-2.5 py-1.5 text-[12px] text-amber-700'>
								Δείχνονται τα 400 πιο πρόσφατα συμβάντα αυτού του διαστήματος.
							</div>
						)}

						{shown.map(event => {
							const { title, detail } = describeActivity(event, { deliveryById });
							const tone = rowTone(event);
							const actor = actorLabel(event);
							const explainable = hasReasoning(event);
							// This action is what brought the automatic check forward. A mark
							// rather than words: it is a property of the action, and spelling
							// it out doubled the length of every clock-in row.
							const pulled = pulledSweepSeconds(event);

							return (
								<div
									key={event.id}
									onClick={explainable ? () => setDecisionOrderId(event.orderId) : undefined}
									className={cn(
										'flex flex-row items-start gap-x-2 border-b border-slate-100 px-2.5 py-1 last:border-b-0',
										explainable && 'cursor-pointer hover:bg-slate-50',
									)}
								>
									<span className='w-[60px] shrink-0 pt-0.5 font-mono text-[12px] text-slate-500'>
										{formatClock(event.createdAt)}
									</span>
									<span className={cn('mt-1.5 size-1.5 shrink-0 rounded-full', tone.dot)} />
									<div className='min-w-0 flex-1'>
										<div className={cn('text-[13px] leading-snug', tone.text)}>
											{pulled && (
												<Zap
													size={12}
													className='mr-1 inline-block shrink-0 fill-amber-300 align-[-1px] text-amber-500'
													// The only place the seconds still appear. On the row
													// they were noise; on demand they are the answer to
													// "what does this mean".
													aria-label={`τράβηξε τον έλεγχο μπροστά κατά ${pulled}δ`}
												>
													<title>{`Τράβηξε τον έλεγχο μπροστά κατά ${pulled}δ`}</title>
												</Zap>
											)}
											{title}
											{actor && <span className='text-slate-400'> ({actor})</span>}
											{/* Said rather than left to be discovered: nothing else on the row
											    suggests it is clickable, and only some rows are. */}
											{explainable && (
												<span className='ml-1 text-[12px] text-slate-400 underline'>γιατί;</span>
											)}
										</div>
										{detail && (
											<div className='text-[12.5px] leading-snug text-slate-400'>{detail}</div>
										)}
									</div>
									{/* The live id, which is what the board and the logs both key on. */}
									{!!event.orderId && (
										<span className='shrink-0 pt-0.5 font-mono text-[12px] text-slate-400'>
											#{event.orderId}
										</span>
									)}
								</div>
							);
						})}
					</div>
				</div>
			)}

			{decisionOrderId && (
				<ActivityDecisionModal
					orderId={decisionOrderId}
					allDeliveries={allDeliveries}
					onClose={() => setDecisionOrderId(null)}
				/>
			)}
		</div>
	);
};

export default ActivityFeed;
